import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  AcademicCapIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BellIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ModernAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalSubjects: 0,
    todayAttendance: 0,
    activeClasses: 0,
    pendingApprovals: 0
  });

  const [loading, setLoading] = useState(true);

  // Sample data for charts
  const attendanceData = [
    { day: 'Mon', present: 85, absent: 15 },
    { day: 'Tue', present: 88, absent: 12 },
    { day: 'Wed', present: 92, absent: 8 },
    { day: 'Thu', present: 87, absent: 13 },
    { day: 'Fri', present: 90, absent: 10 },
    { day: 'Sat', present: 82, absent: 18 },
  ];

  const performanceData = [
    { subject: 'Mathematics', attendance: 92, performance: 85 },
    { subject: 'Physics', attendance: 88, performance: 82 },
    { subject: 'Chemistry', attendance: 90, performance: 88 },
    { subject: 'Computer Science', attendance: 95, performance: 91 },
    { subject: 'English', attendance: 86, performance: 78 },
  ];

  const pieData = [
    { name: 'Present', value: 85, color: '#10b981' },
    { name: 'Absent', value: 10, color: '#ef4444' },
    { name: 'Late', value: 5, color: '#f59e0b' },
  ];

  const recentActivities = [
    { id: 1, action: 'New student enrolled', user: 'John Doe', time: '5 minutes ago', type: 'success' },
    { id: 2, action: 'Attendance marked', user: 'Prof. Smith', time: '10 minutes ago', type: 'info' },
    { id: 3, action: 'Class cancelled', user: 'Dr. Johnson', time: '15 minutes ago', type: 'warning' },
    { id: 4, action: 'New teacher added', user: 'Admin', time: '30 minutes ago', type: 'success' },
    { id: 5, action: 'System maintenance scheduled', user: 'System', time: '1 hour ago', type: 'error' },
  ];

  const upcomingClasses = [
    { id: 1, subject: 'Advanced Mathematics', teacher: 'Prof. Smith', time: '10:00 AM', room: 'A-201', students: 45 },
    { id: 2, subject: 'Physics Lab', teacher: 'Dr. Johnson', time: '11:30 AM', room: 'Lab-3', students: 30 },
    { id: 3, subject: 'Computer Networks', teacher: 'Ms. Davis', time: '2:00 PM', room: 'B-105', students: 38 },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://attendance-backend.rovaidkhan132.workers.dev'}/api/admin/user-counts`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      setStats({
        totalStudents: data.student || 0,
        totalTeachers: data.teacher || 0,
        totalSubjects: data.subjects || 25,
        todayAttendance: 87,
        activeClasses: 12,
        pendingApprovals: 5
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use mock data if API fails
      setStats({
        totalStudents: 250,
        totalTeachers: 25,
        totalSubjects: 30,
        todayAttendance: 87,
        activeClasses: 12,
        pendingApprovals: 5
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, changeType, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft p-6 hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
              {changeType === 'increase' ? <ArrowUpIcon className="w-4 h-4 mr-1" /> : <ArrowDownIcon className="w-4 h-4 mr-1" />}
              <span>{change}% from last week</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const ActivityItem = ({ activity }) => {
    const getActivityIcon = (type) => {
      switch (type) {
        case 'success': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
        case 'warning': return <ExclamationCircleIcon className="w-5 h-5 text-yellow-500" />;
        case 'error': return <XCircleIcon className="w-5 h-5 text-red-500" />;
        default: return <BellIcon className="w-5 h-5 text-blue-500" />;
      }
    };

    return (
      <div className="flex items-start space-x-3 py-3 border-b border-gray-100 last:border-0">
        {getActivityIcon(activity.type)}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{activity.action}</p>
          <p className="text-xs text-gray-500">by {activity.user} • {activity.time}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 font-display">Dashboard Overview</h1>
        <p className="text-gray-600 mt-2">Welcome back, Admin! Here's what's happening today.</p>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 flex gap-4">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Student
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-secondary-600 text-white px-4 py-2 rounded-lg hover:bg-secondary-700 transition-colors duration-200 flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Teacher
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-white text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 flex items-center gap-2 border border-gray-300"
        >
          <CalendarIcon className="w-5 h-5" />
          Schedule Class
        </motion.button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Students"
          value={stats.totalStudents}
          icon={UserGroupIcon}
          change={12}
          changeType="increase"
          color="bg-gradient-to-r from-blue-500 to-blue-600"
        />
        <StatCard
          title="Total Teachers"
          value={stats.totalTeachers}
          icon={AcademicCapIcon}
          change={5}
          changeType="increase"
          color="bg-gradient-to-r from-green-500 to-green-600"
        />
        <StatCard
          title="Today's Attendance"
          value={`${stats.todayAttendance}%`}
          icon={CheckCircleIcon}
          change={3}
          changeType="decrease"
          color="bg-gradient-to-r from-purple-500 to-purple-600"
        />
        <StatCard
          title="Active Classes"
          value={stats.activeClasses}
          icon={ClockIcon}
          change={8}
          changeType="increase"
          color="bg-gradient-to-r from-orange-500 to-orange-600"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Attendance Trend */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-soft p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Weekly Attendance Trend</h2>
            <select className="text-sm border border-gray-300 rounded-lg px-3 py-1">
              <option>This Week</option>
              <option>Last Week</option>
              <option>This Month</option>
            </select>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={attendanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="present" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
              <Area type="monotone" dataKey="absent" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Subject Performance */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-soft p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subject Performance</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="subject" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="attendance" fill="#3b82f6" />
              <Bar dataKey="performance" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Attendance Pie Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-soft p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Today's Attendance</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-around mt-4">
            {pieData.map((item) => (
              <div key={item.name} className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2`} style={{ backgroundColor: item.color }}></div>
                <span className="text-sm text-gray-600">{item.name}: {item.value}%</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-soft p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h2>
          <div className="space-y-1">
            {recentActivities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        </motion.div>

        {/* Upcoming Classes */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-soft p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Classes</h2>
          <div className="space-y-3">
            {upcomingClasses.map((cls) => (
              <div key={cls.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{cls.subject}</p>
                    <p className="text-sm text-gray-500">{cls.teacher}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500 flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        {cls.time}
                      </span>
                      <span className="text-xs text-gray-500">Room: {cls.room}</span>
                    </div>
                  </div>
                  <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full">
                    {cls.students} students
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ModernAdminDashboard;
